import { ServiceSubTypesEnum, ServiceTypesEnum } from "@propertelligent/client-api";

type EnumOptions = {
  humanReadable?: boolean;
  fallback?: string;
};

function baseGetEnumNames<T extends Record<number, string>>(
  enumObj: T,
  ids?: number[],
  options?: EnumOptions
): string {
  if (!ids?.length) return options?.fallback ?? '';

  return ids
    .map(id => enumObj[id])
    .filter(Boolean)
    .map(name =>
      options?.humanReadable
        ? name.replace(/([A-Z])/g, ' $1').trim()
        : name
    )
    .join(' | ');
}

/* 🔥 Ready-to-use helpers */
export const getServiceTypeNames = (
  ids?: number[],
  options?: EnumOptions
) => baseGetEnumNames(ServiceTypesEnum, ids, options);

export const getServiceSubTypeNames = (
  ids?: number[],
  options?: EnumOptions
) => baseGetEnumNames(ServiceSubTypesEnum, ids, options);

// Builds {Id, Name} dropdown options from a numeric enum's own reverse-mapping,
// so the label always matches the enum member name instead of being duplicated
// as a separate hardcoded string.
export function getEnumOptions<T extends Record<number, string>>(
  enumObj: T,
  ids: number[]
): { Id: number; Name: string }[] {
  return ids
    .filter((id): id is number => id != null && enumObj[id] != null)
    .map((id) => ({ Id: id, Name: enumObj[id] }));
}

// TODO(feature/plaid-pay-status-ux): interim local mirror of the backend's
// TransferFailureReasonEnum (Propertelligent.Common/Enums/Payments/TransferFailureReasonEnum.cs),
// which is not yet published in @propertelligent/client-api. The values are NUMERIC because the
// API serializes every C# enum as its integer value -- exactly like the already-generated
// PayDueAccountStatusEnum -- so these are the on-the-wire contract, not member-name strings.
// Once the client republishes, delete this declaration and import the generated
// TransferFailureReasonEnum instead; every usage below and in
// src/hooks/usePaymentConfirmationPoll.ts is a 1:1 swap.
export enum TransferFailureReason {
  None = 0,
  SourceAccountNotConnected = 1,
  RecipientAccountNotConnected = 2,
  InsufficientFunds = 3,
  DeclinedByBank = 4,
  Unknown = 5,
  AccountClosedOrInvalid = 6,
  Unauthorized = 7,
  PaymentStopped = 8,
}

// Keyed by the numeric values above rather than a generated enum object like baseGetEnumNames
// expects; once the client republishes, this map is unchanged.
const TRANSFER_FAILURE_REASON_LABELS: Record<number, string> = {
  [TransferFailureReason.SourceAccountNotConnected]: "Your payment account isn't connected",
  [TransferFailureReason.RecipientAccountNotConnected]:
    "The recipient hasn't connected a payment account",
  [TransferFailureReason.InsufficientFunds]: "Insufficient funds",
  [TransferFailureReason.DeclinedByBank]: "Declined by bank",
  [TransferFailureReason.Unknown]:
    "An unexpected error occurred while processing the payment",
  [TransferFailureReason.AccountClosedOrInvalid]:
    "The bank account is closed or invalid",
  [TransferFailureReason.Unauthorized]:
    "The payment was not authorized by the account holder",
  [TransferFailureReason.PaymentStopped]:
    "The account holder stopped the payment",
};

// Human-readable text for a single TransferFailureReason value. Returns an empty string for
// None (0), null/undefined, and any unrecognized value so callers can fall back to their own
// generic copy (e.g. "Payment attempt failed" with no appended reason). The falsy guard below
// deliberately covers None, since TransferFailureReason.None is 0.
export function getTransferFailureReasonLabel(
  reason?: TransferFailureReason | number | null,
): string {
  if (!reason) return "";
  return TRANSFER_FAILURE_REASON_LABELS[reason] ?? "";
}
